<template>
<transition name="fade" appear>
  <div class="newPassword">
    <Preloader v-if="isLoading" />
    <img
      class="newPassword__logo"
      src="@/assets/images/brand/logo_login.svg"
      alt="Early Stage"
    />
    <div v-if="!success" class="newPassword__login">
      <h2 class="newPassword__title">{{ $t("auth.new_password_request_heading") }}</h2>
      <div class="newPassword__desc"><p v-html="$t('auth.new_password_request_text_1')"></p></div>
      <div class="newPassword__desc"><p class="md:whitespace-pre">{{ $t("auth.new_password_request_text_2") }}</p></div>
      <form @submit.prevent="request">
        <input
          type="email"
          name="email"
          :placeholder="$t('auth.new_password_request_email')"
          :class="{ validateError: isError }"
          required
          v-model.trim="$v.email.$model"
          @input="delayTouch($v.email)"
          autocomplete="email"
        />
        <span v-if="isError" class="newPassword__loginError mb-1">{{ errorText }}</span>
        <div
          class="newPassword__validError"
          v-if="$v.email.$dirty && !$v.email.required"
        >
          {{ $t("validation.email.required") }}
        </div>
        <div
          class="newPassword__validError"
          v-if="$v.email.$dirty && !$v.email.email"
        >
          {{ $t("validation.email.notValid") }}
        </div>
        <Button class="btn_primary mx-3" :disabled="isDisabled">{{
          $t("auth.send")
        }}</Button>
      </form>
    </div>
    <div v-else class="newPassword__login newPassword__login--success">
      <h2 class="newPassword__title">{{ $t("auth.new_password_request_success_heading") }}</h2>
      <div class="newPassword__desc"><p v-html="$t('auth.new_password_request_success_info')"></p></div>
      <div class="h-32 md:h-48">
        <img class="mt-4" src="@/assets/images/reset/sent.svg">
      </div>
    </div>
    <div>
      <div class="newPassword__politics">
        <a href="/Polityka_Prywatności_ES_Online.pdf" target="_blank" class="forgotPassword__policy--item">{{ $t('dashboard.privacy_and_cookies') }}</a>
        <a :href="REGULATIONS_PATH" target="_blank" class="forgotPassword__policy--item">{{ $t('dashboard.regulations') }}</a>
      </div>
    </div>
    <div class="newPassword__kids mb-6 md:mb-0">
      <div class="kid_first-wrapper">
        <img
          class="kid_first-desktop"
          src="@/assets/images/welcome/kid_first-desktop.svg"
        />
      </div>
      <div class="kid_second-wrapper">
        <img
          class="kid_second-desktop"
          src="@/assets/images/welcome/kid_second-desktop.svg"
        />
      </div>
    </div>
    <div v-if="!success" class="newPassword__footer">
      <p>{{ $t("auth.new_password_request_footer") }}</p>
      <p><a class="font-bold" href="https://earlystage.pl/pl/zapisy">{{ $t("auth.register") }}</a></p>
    </div>
  </div>
</transition>
</template>

<script>
import { validationMixin } from 'vuelidate';
import {
  required, email,
} from 'vuelidate/lib/validators';
import { mapActions, mapState } from 'vuex';

import Button from '@/components/Form/BaseButton.vue';
import Preloader from '@/components/Preloader.vue';

const touchMap = new WeakMap();

export default {
  name: 'FirstLogin',

  components: {
    Button,
    Preloader,
  },

  props: {
    initialEmail: {
      type: String,
      default: null,
    },
  },

  mixins: [validationMixin],
  validations: {
    email: {
      required,
      email,
    },
  },

  data() {
    return {
      email: this.initialEmail || '',
      isError: false,
      errorText: '',
      isDisabled: false,
      success: false,
    };
  },

  computed: {
    ...mapState('layout', ['isLoading']),
  },

  watch: {
    email() {
      this.isError = false;
    },
  },

  created() {
    this.$store.commit('layout/finishLoading');
  },

  methods: {
    ...mapActions('auth', ['requestNewPassword']),
    delayTouch($v) {
      $v.$reset();
      if (touchMap.has($v)) {
        clearTimeout(touchMap.get($v));
      }
      touchMap.set($v, setTimeout($v.$touch, 1000));
    },

    async request() {
      this.isDisabled = true;
      this.$v.$touch();
      try {
        if (!this.$v.$invalid) {
          this.$store.commit('layout/startLoading');
          await this.requestNewPassword({ email: this.email });
          this.success = true;
        }
      } catch (e) {
        console.error(e);
        this.isError = true;
        if (e.response && e.response.data) {
          this.errorText = e.response.data.errors.fields.email;
        } else {
          this.errorText = this.$t('auth.new_password_error');
        }
      } finally {
        this.isDisabled = false;
        this.$store.commit('layout/finishLoading');
      }
    },

  },
};
</script>

<style lang="scss" scoped>
input:-webkit-autofill,
input:-webkit-autofill:hover,
input:-webkit-autofill:active,
input:-webkit-autofill:focus {
  background-color: #f0f9fe !important;
  -webkit-box-shadow: 0 0 0 1000px #f0f9fe inset !important;
}

input::placeholder {
  @apply text-black opacity-100;
}

.newPassword {
  @apply relative flex flex-col items-center mx-auto my-0 text-center;
  min-height: 100vh;
  max-width: 600px;
  h2 {
    color: #040404;
    line-height: 1.3;
  }
  @media screen and (min-width: 768px) {
    max-width: 1200px;
  }
  &__logo {
    @apply h-auto block mx-auto;
    max-width: 260px;
    margin-top: 30px;
    @media screen and (min-width: 768px) {
      max-width: 300px;
      margin-top: 84px;
    }
  }
  &__loginError {
    @apply text-red mb-5;
    @media screen and (min-width: 768px) {
      @apply text-xl;
    }
  }
  &__validError {
    @apply text-red mb-4;
  }
  &__invalid-token {
    @apply relative mx-auto;
    line-height: 1.2;
    padding: 0 10px;
    margin-top: 20px;
    margin-bottom: 20px;
    @media screen and (min-width: 768px) {
      margin-top: 120px;
      margin-bottom: 100px;
    }
  }
  &__login {
    @apply relative mx-auto;
    width: 260px;
    margin-top: 10px;
    margin-bottom: 20px;
    display: flex;
    justify-content: center;
    align-items: center;
    flex-direction: column;
    @media screen and (min-width: 768px) {
      width: 751px;
      margin-top: 5px;
      margin-bottom: 20px;
    }
    &--success {
      max-width: 520px;
    }
    form {
      flex-flow: column;
      height: 170px;
      @media screen and (min-width: 568px) {
        width: 380px;
      }
      @apply mt-5 flex;
      input {
        @apply bg-blue-light-main mx-3 mb-4;
        padding: 12px 20px;
        border: 2px solid #1b181a;
        border-radius: 100px;
        box-shadow: 0 1px 3px rgba(0, 0, 0, 0.12), 0 1px 2px rgba(0, 0, 0, 0.24);
        transition: 0.5s box-shadow;
        &.validateError {
          border: 2px solid red;
          box-shadow: 0px 0px 0px 1px red;
          &:focus {
            box-shadow: 0px 0px 0px 1px red;
          }
        }
        &:focus {
          @apply bg-blue-light-main;
          box-shadow: 0px 0px 0px 1px rgba(0, 0, 0, 1),
            0 7px 14px rgba(0, 0, 0, 0.1), 0 5px 5px rgba(0, 0, 0, 0.06);
        }
        @media screen and (min-width: 768px) {
          @apply text-2xl;
          padding: 18px 30px;
        }
      }
      .btn_primary {
        @media screen and (min-width: 768px) {
          padding-top: 1.125rem;
          padding-bottom: 1.125rem;
        }
      }
    }
  }
  &__title {
    @apply text-xl;
    font-weight: 700;
    margin-top: 1.5rem;
    margin-bottom: 1rem;
    @media screen and (min-width: 768px) {
      @apply text-3xl;
    }
  }
  &__desc {
    @apply leading-tight;
    margin-top: 1rem;
    margin-bottom: 1rem;
    @media screen and (min-width: 768px) {
      @apply text-xl;
      margin-top: .75rem;
      margin-bottom: 1rem;
    }
    p {
      line-height: 24px;
      @media screen and (min-width: 768px) {
        line-height: 26px;
      }
    }
    a {
      @apply text-blue no-underline;
    }
  }
  &__politics {
    @apply flex flex-col pb-4 pt-3 text-sm;
    @media screen and (min-width: 768px) {
      @apply fixed flex-row mx-6 pt-8 text-base;
      right: 0;
      top: 0;
    }
    &--item,
    a {
      @apply flex justify-center items-center mx-3 mb-2 no-underline;
      transition: all 0.3s cubic-bezier(0.25, 0.8, 0.25, 1);
      &:hover {
        @apply text-grey-darkest;
      }
      span {
        @apply mx-2;
      }
    }
  }
  &__kids {
    @apply flex items-start px-3;
    width: 100%;
    @media screen and (min-width: 768px) {
      @apply -mt-6;
    }
    @media screen and (min-width: 992px) {
      @apply -mt-12;
    }
    div {
      @apply px-3;
    }
    .kid_second-desktop {
      width: 170px;
      @apply mt-5;
    }
  }
  &__footer {
    @apply mb-3;
    p {
      font-size: 16px;
      @apply mb-2 pb-1;
    }
    a {
      @apply no-underline;
      &:hover, &:focus {
        @apply no-underline;
      }
    }
  }
}
</style>
